import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiChevronDown } from 'react-icons/fi';
import { useLanguage } from '../context/LanguageContext';

const FilterSection = styled.div`
  margin-bottom: 24px;
`;

const FilterTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
`;

const SliderContainer = styled.div`
  width: 100%;
  padding: 10px 0;
`;

const SliderWrapper = styled.div`
  position: relative;
  height: 6px;
  margin: 20px 0;
  direction: ltr;
`;

const SliderTrack = styled.div`
  position: absolute;
  width: 100%;
  height: 6px;
  background: #e5e7eb;
  border-radius: 3px;
  top: 50%;
  transform: translateY(-50%);
`;

const SliderRange = styled.div`
  position: absolute;
  height: 6px;
  background: linear-gradient(90deg, #10b981 0%, #5efba9 100%);
  border-radius: 3px;
  top: 50%;
  transform: translateY(-50%);
`;

const SliderInput = styled.input`
  position: absolute;
  width: 100%;
  height: 6px;
  background: none;
  pointer-events: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  top: 50%;
  transform: translateY(-50%);

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    pointer-events: all;
    width: 18px;
    height: 18px;
    background: white;
    border: 3px solid #10b981;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
    }

    &:active {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
    }
  }

  &::-moz-range-thumb {
    pointer-events: all;
    width: 18px;
    height: 18px;
    background: white;
    border: 3px solid #10b981;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.2s;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

    &:hover {
      transform: scale(1.15);
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
    }

    &:active {
      transform: scale(1.05);
      box-shadow: 0 2px 8px rgba(16, 185, 129, 0.4);
    }
  }
`;

const PriceLabels = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 12px;
  gap: 8px;
  direction: ltr;
`;

const PriceLabel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${props => props.align || 'flex-start'};
  flex: 1;
  direction: ${props => props.dir || 'ltr'};
`;

const LabelText = styled.span`
  font-size: 11px;
  color: #9ca3af;
  margin-bottom: 2px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const PriceValue = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1f2937;
  
  @media (max-width: 640px) {
    font-size: 13px;
  }
`;

const PriceSeparator = styled.span`
  color: #e5e7eb;
  font-weight: 600;
  font-size: 14px;
  user-select: none;
  margin: 0 4px;
`;

const InlineSliderContainer = styled.div`
  width: 100%;
  padding: 0;
`;

const InlineSliderWrapper = styled.div`
  position: relative;
  height: 6px;
  margin: 8px 0;
  direction: ltr;
`;

/**
 * PriceRangeSlider Component
 * 
 * A dual-range slider for price filtering
 * 
 * @param {Object} props
 * @param {number} props.min - Minimum value for the slider range (defaults to 0)
 * @param {number} props.max - Maximum value for the slider range (defaults to 10000)
 * @param {Object} props.value - Current selected range {min, max}
 * @param {Function} props.onChange - Callback when range changes
 * @param {boolean} props.inline - If true, renders without wrapper (for inline use)
 */
function PriceRangeSlider({ value, onChange, min = 0, max = 10000, inline = false }) {
  const { isArabic } = useLanguage();
  const [localMin, setLocalMin] = useState(value?.min || min);
  const [localMax, setLocalMax] = useState(value?.max || max);

  useEffect(() => {
    if (value) {
      setLocalMin(value.min || min);
      setLocalMax(value.max || max);
    }
  }, [value?.min, value?.max, min, max]);

  const handleMinChange = (e) => {
    const newMin = Math.min(parseFloat(e.target.value), localMax - 1);
    setLocalMin(newMin);
    onChange({ min: newMin, max: localMax });
  };

  const handleMaxChange = (e) => {
    const newMax = Math.max(parseFloat(e.target.value), localMin + 1);
    setLocalMax(newMax);
    onChange({ min: localMin, max: newMax });
  };

  const minPercent = ((localMin - min) / (max - min)) * 100;
  const maxPercent = ((localMax - min) / (max - min)) * 100;

  const SliderContent = (
    <>
      {inline ? (
        <InlineSliderWrapper>
          <SliderTrack />
          <SliderRange 
            style={{
              left: `${minPercent}%`,
              right: `${100 - maxPercent}%`
            }}
          />
          <SliderInput
            type="range"
            min={min}
            max={max}
            value={localMin}
            onChange={handleMinChange}
            style={{ zIndex: localMin > max - 100 ? 5 : 3 }}
          />
          <SliderInput
            type="range"
            min={min}
            max={max}
            value={localMax}
            onChange={handleMaxChange}
            style={{ zIndex: 4 }}
          />
        </InlineSliderWrapper>
      ) : (
        <SliderContainer>
          <SliderWrapper>
            <SliderTrack />
            <SliderRange 
              style={{
                left: `${minPercent}%`,
                right: `${100 - maxPercent}%`
              }}
            />
            <SliderInput
              type="range"
              min={min}
              max={max}
              value={localMin}
              onChange={handleMinChange}
              style={{ zIndex: localMin > max - 100 ? 5 : 3 }}
            />
            <SliderInput
              type="range"
              min={min}
              max={max}
              value={localMax}
              onChange={handleMaxChange}
              style={{ zIndex: 4 }}
            />
          </SliderWrapper>
          <PriceLabels>
            <PriceLabel align="flex-start" dir={isArabic ? 'rtl' : 'ltr'}>
              <LabelText>{isArabic ? 'الحد الأدنى' : 'Min'}</LabelText>
              <PriceValue>{Math.round(localMin)} OMR</PriceValue>
            </PriceLabel>
            <PriceSeparator>-</PriceSeparator>
            <PriceLabel align="flex-end" dir={isArabic ? 'rtl' : 'ltr'}>
              <LabelText>{isArabic ? 'الحد الأقصى' : 'Max'}</LabelText>
              <PriceValue>{Math.round(localMax)} OMR</PriceValue>
            </PriceLabel>
          </PriceLabels>
        </SliderContainer>
      )}
    </>
  );

  if (inline) {
    return <InlineSliderContainer>{SliderContent}</InlineSliderContainer>;
  }

  return (
    <FilterSection>
      <FilterTitle>
        <span>{isArabic ? 'نطاق السعر' : 'Price Range'}</span>
        <FiChevronDown />
      </FilterTitle>
      {SliderContent}
    </FilterSection>
  );
}

export default PriceRangeSlider;
